import type { ModuleMetadataWorkingMode } from '@/common/api/models/moduleMetadataWorkingMode';
import type { WorkingModeActivity } from '@/common/api/models/workingModeActivity';
import type { WorkingModeDiff } from '@/common/api/models/workingModeDiff';
import type { Task } from '@/common/api/models/task';
import { WorkingModeActivityPhase } from '@/common/api/models/workingModeActivityPhase';

export type WorkingModeWire = NonNullable<ModuleMetadataWorkingMode>;

export const WORKING_MODE_OPTIONS: WorkingModeWire[] = [
  'classic_essays',
  'ai_tutor_oral',
  'custom',
];

export const MODE_LABEL_KEYS: Record<WorkingModeWire, string> = {
  custom: 'lecturer.moduleEditor.workingMode.modes.custom',
  classic_essays: 'lecturer.moduleEditor.workingMode.modes.classicEssays',
  ai_tutor_oral: 'lecturer.moduleEditor.workingMode.modes.aiTutorOral',
};

/** Static rule bullets shown in the in-sync info popover (not the apply delta). */
export const MODE_INFO_RULE_KEYS: Record<Exclude<WorkingModeWire, 'custom'>, string[]> = {
  classic_essays: [
    'lecturer.moduleEditor.workingMode.info.rules.progressionFree',
    'lecturer.moduleEditor.workingMode.info.rules.gssPresentFreeLevel2',
    'lecturer.moduleEditor.workingMode.info.rules.oralExamAbsent',
    'lecturer.moduleEditor.workingMode.info.rules.essaysAiTutorOff',
  ],
  ai_tutor_oral: [
    'lecturer.moduleEditor.workingMode.info.rules.progressionGated',
    'lecturer.moduleEditor.workingMode.info.rules.minLevelsTenantDefaults',
    'lecturer.moduleEditor.workingMode.info.rules.autogradeOn',
    'lecturer.moduleEditor.workingMode.info.rules.oralExamPresent',
    'lecturer.moduleEditor.workingMode.info.rules.oralExamConfigured',
    'lecturer.moduleEditor.workingMode.info.rules.oralQuestionsGenerated',
    'lecturer.moduleEditor.workingMode.info.rules.gssPresentFree',
    'lecturer.moduleEditor.workingMode.info.rules.essaysAiTutor',
    'lecturer.moduleEditor.workingMode.info.rules.essayInheritPolicy',
    'lecturer.moduleEditor.workingMode.info.rules.essayTransferTaskOff',
  ],
};

export type WorkingModeBlockVisualState =
  | 'custom'
  | 'nothing_to_change'
  | 'drifted'
  | 'applying'
  | 'in_sync'
  | 'version_locked';

/** Non-draft workflow states share the Working Mode create-draft gate. */
export type WorkingModeLockedWorkflowState =
  | 'pending_approval'
  | 'published'
  | 'retired';

export type ApplyChecklistTone = 'action' | 'confirm';

export interface ApplyChecklistItem {
  id: string;
  tone: ApplyChecklistTone;
  /** i18n key under lecturer.moduleEditor.workingMode.dialog.checklist.* */
  messageKey: string;
  messageParams?: Record<string, string | number>;
}

export interface ApplyChecklistSection {
  id: string;
  titleKey: string;
  items: ApplyChecklistItem[];
}

export interface ApplyChecklistModel {
  sections: ApplyChecklistSection[];
  /** Raw activity count — still what the server will process. */
  changeCount: number;
  phaseCount: number;
}

export const countDiffChanges = (activities: WorkingModeActivity[] | undefined): number => (
  activities?.length ?? 0
);

export const groupActivitiesByPhase = (
  activities: WorkingModeActivity[] | undefined,
): Record<string, WorkingModeActivity[]> => {
  const grouped: Record<string, WorkingModeActivity[]> = {
    [WorkingModeActivityPhase.module_policies]: [],
    [WorkingModeActivityPhase.structure]: [],
    [WorkingModeActivityPhase.essays]: [],
  };
  (activities ?? []).forEach((activity) => {
    grouped[activity.phase]?.push(activity);
  });
  return grouped;
};

export const countNonEmptyPhases = (grouped: Record<string, WorkingModeActivity[]>): number => (
  Object.values(grouped).filter((items) => items.length > 0).length
);

export const countGovernedActivities = (
  tasks: Task[] | undefined,
  mode: WorkingModeWire,
): number => {
  if (!tasks?.length || mode === 'custom') return 0;
  if (mode === 'classic_essays') return tasks.length;
  return tasks.filter((task) => task.type === 'essay' || task.type === 'event').length;
};

export const summarizeDeltaLedger = (activities: WorkingModeActivity[] | undefined) => {
  const grouped = groupActivitiesByPhase(activities);
  const policyCount = grouped[WorkingModeActivityPhase.module_policies].length;
  const structureCount = grouped[WorkingModeActivityPhase.structure].length;
  const essayActivities = grouped[WorkingModeActivityPhase.essays];
  const essayTaskIds = new Set<string>();
  essayActivities.forEach((activity) => {
    activity.entityRefs?.forEach((ref) => essayTaskIds.add(ref.taskId));
  });
  const essayTaskCount = essayTaskIds.size || essayActivities.length;

  return {
    policyCount, structureCount, essayTaskCount, essayActivities,
  };
};

export const resolveBlockVisualState = (inputs: {
  persistedMode: WorkingModeWire;
  isDraft: boolean;
  isApplying: boolean;
  diff?: WorkingModeDiff | null;
  governedActivityCount: number;
}): WorkingModeBlockVisualState => {
  const {
    persistedMode, isDraft, isApplying, diff, governedActivityCount,
  } = inputs;

  if (!isDraft) return 'version_locked';
  if (isApplying) return 'applying';
  if (persistedMode === 'custom') return 'custom';
  if (!diff || diff.empty) {
    return governedActivityCount > 0 ? 'in_sync' : 'nothing_to_change';
  }
  return 'drifted';
};

export const resolveLockedWorkflowState = (
  workflowState: string | undefined,
): WorkingModeLockedWorkflowState => {
  if (workflowState === 'pending_approval' || workflowState === 'retired') {
    return workflowState;
  }
  return 'published';
};

export const formatPolicyValue = (value: unknown): string => {
  if (value === true || value === 'true') return 'On';
  if (value === false || value === 'false') return 'Off';
  if (value === 'free') return 'Free';
  if (value === 'gated') return 'Gated';
  if (value === 'auto') return 'AI Tutor on';
  if (value === 'manual') return 'Manual';
  if (value === 'inherit') return 'Inherit';
  if (value === 'oral_exam') return 'Oral Exam';
  if (value === 'group_study_60_15') return 'Group study session';
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>;
    if (record.use_tenant_defaults === true || record.useTenantDefaults === true) {
      return 'Tenant defaults';
    }
    return JSON.stringify(value);
  }
  return String(value);
};

/**
 * Mirror backend `default_completion_min_levels`: prefer ``good`` when defined,
 * otherwise the lowest passing performance level (≥ 50%), applied to every
 * evaluation dimension (including system-managed formal). Returns a lecturer-facing
 * summary like "Description: Good · Formal: Good".
 */
export const resolveTenantDefaultMinLevelsSummary = (inputs: {
  editableDimensionKeys: string[];
  performanceLevels: Array<{ id: string; label: string; performancePercent: number }>;
  /** When set, include these keys too (e.g. system-managed formal). */
  allDimensionKeys?: string[];
}): string => {
  const {
    editableDimensionKeys,
    performanceLevels,
    allDimensionKeys,
  } = inputs;
  const dimensionKeys = allDimensionKeys?.length
    ? allDimensionKeys
    : editableDimensionKeys;
  if (!dimensionKeys.length || !performanceLevels.length) return '';

  const sortedLevels = [...performanceLevels].sort(
    (a, b) => a.performancePercent - b.performancePercent,
  );
  const goodLevel = sortedLevels.find((level) => level.id === 'good');
  const passing = goodLevel
    ?? sortedLevels.find((level) => level.performancePercent >= 0.5)
    ?? sortedLevels[0];
  if (!passing) return '';

  return [...dimensionKeys]
    .sort()
    .map((key) => {
      const dimLabel = key.charAt(0).toUpperCase() + key.slice(1);
      return `${dimLabel}: ${passing.label}`;
    })
    .join(' · ');
};

const taskHasObjectives = (task: Task): boolean => {
  const dimensions = task.analysis?.dimensions;
  if (!dimensions || typeof dimensions !== 'object') return false;
  return Object.values(dimensions).some(
    (dim) => !!dim?.applicable && Array.isArray(dim.demands) && dim.demands.length > 0,
  );
};

const taskHasRubric = (task: Task): boolean => {
  const { rubric } = task;
  if (!rubric || typeof rubric !== 'object') return false;
  if ('dimensions' in rubric && Array.isArray(rubric.dimensions)) {
    return rubric.dimensions.length > 0;
  }
  // Legacy V1 rubrics are dimension→level maps without a dimensions array.
  return Object.keys(rubric).length > 0;
};

const essayTasksFrom = (tasks: Task[] | undefined): Task[] => (
  (tasks ?? []).filter((task) => task.type === 'essay')
);

const activityTaskIds = (activities: WorkingModeActivity[]): Set<string> => {
  const ids = new Set<string>();
  activities.forEach((activity) => {
    activity.entityRefs?.forEach((ref) => {
      if (ref.taskId) ids.add(ref.taskId);
    });
  });
  return ids;
};

const policyActionItem = (
  activity: WorkingModeActivity,
  options?: { minLevelsSummary?: string },
): ApplyChecklistItem | null => {
  switch (activity.id) {
    case 'module.progression_gated':
      return {
        id: activity.id,
        tone: 'action',
        messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.progressionToGated',
        messageParams: {
          current: formatPolicyValue(activity.current),
          desired: formatPolicyValue(activity.desired),
        },
      };
    case 'module.progression_free':
      return {
        id: activity.id,
        tone: 'action',
        messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.progressionToFree',
        messageParams: {
          current: formatPolicyValue(activity.current),
          desired: formatPolicyValue(activity.desired),
        },
      };
    case 'module.autograde_on':
      return {
        id: activity.id,
        tone: 'action',
        messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.autogradeOn',
      };
    case 'module.autograde_off':
      return {
        id: activity.id,
        tone: 'action',
        messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.autogradeOff',
      };
    case 'module.min_levels_present':
      return {
        id: activity.id,
        tone: 'action',
        messageKey: options?.minLevelsSummary
          ? 'lecturer.moduleEditor.workingMode.dialog.checklist.minLevelsExplicit'
          : 'lecturer.moduleEditor.workingMode.dialog.checklist.minLevelsFallback',
        messageParams: options?.minLevelsSummary
          ? { levels: options.minLevelsSummary }
          : undefined,
      };
    default:
      return null;
  }
};

/**
 * Collapse per-task wire activities into a readable confirm-style checklist.
 * Dedupes GSS free-override rows and surfaces essay objectives/rubric/AI Tutor counts.
 */
export const buildApplyChecklist = (
  activities: WorkingModeActivity[] | undefined,
  tasks?: Task[],
  options?: { minLevelsSummary?: string; targetMode?: WorkingModeWire },
): ApplyChecklistModel => {
  const list = activities ?? [];
  const grouped = groupActivitiesByPhase(list);
  const sections: ApplyChecklistSection[] = [];

  const policyItems = grouped[WorkingModeActivityPhase.module_policies]
    .map((activity) => policyActionItem(activity, options))
    .filter((item): item is ApplyChecklistItem => item !== null);
  if (policyItems.length > 0) {
    sections.push({
      id: 'policies',
      titleKey: 'lecturer.moduleEditor.workingMode.dialog.phases.policies',
      items: policyItems,
    });
  }

  const structureItems: ApplyChecklistItem[] = [];
  const structureActs = grouped[WorkingModeActivityPhase.structure];
  if (structureActs.some((activity) => activity.id === 'structure.oral_exam_present')) {
    structureItems.push({
      id: 'structure.oral_exam_present',
      tone: 'action',
      messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.addOralExam',
    });
  }
  const oralConfiguredCount = structureActs.filter(
    (activity) => activity.id === 'structure.oral_exam_configured',
  ).length;
  if (oralConfiguredCount > 0) {
    structureItems.push({
      id: 'structure.oral_exam_configured',
      tone: 'action',
      messageKey: oralConfiguredCount === 1
        ? 'lecturer.moduleEditor.workingMode.dialog.checklist.configureOralExamOne'
        : 'lecturer.moduleEditor.workingMode.dialog.checklist.configureOralExamMany',
      messageParams: { count: oralConfiguredCount },
    });
  }
  if (structureActs.some((activity) => activity.id === 'oral.question_pool_present')) {
    structureItems.push({
      id: 'oral.question_pool_present',
      tone: 'action',
      messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.generateOralQuestions',
    });
  }
  const oralRemoveCount = structureActs.filter((activity) => activity.id === 'structure.oral_exam_absent').length;
  if (oralRemoveCount > 0) {
    structureItems.push({
      id: 'structure.oral_exam_absent',
      tone: 'action',
      messageKey: oralRemoveCount === 1
        ? 'lecturer.moduleEditor.workingMode.dialog.checklist.removeOralExamOne'
        : 'lecturer.moduleEditor.workingMode.dialog.checklist.removeOralExamMany',
      messageParams: { count: oralRemoveCount },
    });
  }
  if (structureActs.some((activity) => activity.id === 'structure.gss_present')) {
    structureItems.push({
      id: 'structure.gss_present',
      tone: 'action',
      messageKey: options?.targetMode === 'classic_essays'
        ? 'lecturer.moduleEditor.workingMode.dialog.checklist.addGroupStudyLevel2'
        : 'lecturer.moduleEditor.workingMode.dialog.checklist.addGroupStudy',
    });
  }
  const gssFreeCount = structureActs.filter((activity) => activity.id === 'structure.gss_not_gating').length;
  if (gssFreeCount > 0) {
    structureItems.push({
      id: 'structure.gss_not_gating',
      tone: 'action',
      messageKey: gssFreeCount === 1
        ? 'lecturer.moduleEditor.workingMode.dialog.checklist.gssCompletionFreeOne'
        : 'lecturer.moduleEditor.workingMode.dialog.checklist.gssCompletionFreeMany',
      messageParams: { count: gssFreeCount },
    });
  }
  if (structureItems.length > 0) {
    sections.push({
      id: 'structure',
      titleKey: 'lecturer.moduleEditor.workingMode.dialog.phases.taskSettings',
      items: structureItems,
    });
  }

  const essayActs = grouped[WorkingModeActivityPhase.essays];
  const essays = essayTasksFrom(tasks);
  const needsObjectivesIds = activityTaskIds(
    essayActs.filter((activity) => activity.id === 'essay.objectives_present'),
  );
  const needsRubricIds = activityTaskIds(
    essayActs.filter((activity) => activity.id === 'essay.rubric_present'),
  );
  const needsAiTutorIds = activityTaskIds(
    essayActs.filter((activity) => (
      activity.id === 'essay.ai_tutor_auto' || activity.id === 'essay.rubric_present'
    )),
  );
  const needsAiTutorOffIds = activityTaskIds(
    essayActs.filter((activity) => activity.id === 'essay.ai_tutor_off'),
  );
  const needsInheritIds = activityTaskIds(
    essayActs.filter((activity) => activity.id === 'essay.inherits_module_policy'),
  );
  const needsTransferOffIds = activityTaskIds(
    essayActs.filter((activity) => activity.id === 'essay.transfer_task_off'),
  );

  const essayItems: ApplyChecklistItem[] = [];

  if (needsObjectivesIds.size > 0) {
    essayItems.push({
      id: 'essay.objectives.missing',
      tone: 'action',
      messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.generateMissingObjectives',
      messageParams: { count: needsObjectivesIds.size },
    });
  }
  if (needsRubricIds.size > 0) {
    essayItems.push({
      id: 'essay.rubric',
      tone: 'action',
      messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.generateMissingRubrics',
      messageParams: { count: needsRubricIds.size },
    });
  }
  if (needsAiTutorIds.size > 0) {
    essayItems.push({
      id: 'essay.ai_tutor',
      tone: 'action',
      messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.turnAiTutorOn',
      messageParams: { count: needsAiTutorIds.size },
    });
  }
  if (needsAiTutorOffIds.size > 0) {
    essayItems.push({
      id: 'essay.ai_tutor_off',
      tone: 'action',
      messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.turnAiTutorOff',
      messageParams: { count: needsAiTutorOffIds.size },
    });
  }

  if (needsInheritIds.size > 0) {
    essayItems.push({
      id: 'essay.inherits_module_policy',
      tone: 'action',
      messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.essayInheritPolicy',
      messageParams: { count: needsInheritIds.size },
    });
  }
  if (needsTransferOffIds.size > 0) {
    essayItems.push({
      id: 'essay.transfer_task_off',
      tone: 'action',
      messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.essayTransferTaskOff',
      messageParams: { count: needsTransferOffIds.size },
    });
  }

  if (essays.length > 0) {
    const objectivesReady = essays.filter(taskHasObjectives).length;
    const rubricsReady = essays.filter(taskHasRubric).length;
    const aiTutorReady = essays.filter(
      (task) => task.evaluation?.mode === 'auto' && !needsAiTutorIds.has(task.id),
    ).length;

    if (objectivesReady > 0) {
      essayItems.push({
        id: 'essay.objectives.ready',
        tone: 'confirm',
        messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.objectivesReady',
        messageParams: { count: objectivesReady },
      });
    }
    if (rubricsReady > 0) {
      essayItems.push({
        id: 'essay.rubric.ready',
        tone: 'confirm',
        messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.rubricsReady',
        messageParams: { count: rubricsReady },
      });
    }
    if (aiTutorReady > 0) {
      essayItems.push({
        id: 'essay.ai_tutor.ready',
        tone: 'confirm',
        messageKey: 'lecturer.moduleEditor.workingMode.dialog.checklist.aiTutorReady',
        messageParams: { count: aiTutorReady },
      });
    }
  }

  if (essayItems.length > 0) {
    sections.push({
      id: 'essays',
      titleKey: 'lecturer.moduleEditor.workingMode.dialog.phases.essays',
      items: essayItems,
    });
  }

  return {
    sections,
    changeCount: list.length,
    phaseCount: countNonEmptyPhases(grouped),
  };
};
